import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, List, ListItem } from "@mui/material";
import { cloneDeep } from "lodash";
import MDBox from "components/MDBox";
import MultiSelectDropdown from "components/SettingFields/atoms/MultiSelectDropdown";
import MultiBooleanSetting from "components/SettingFields/atoms/MultiBooleanSetting";
import SingleSelectDropdown from "components/SettingFields/atoms/SingleSelectDropdown";
import MDTypography from "components/MDTypography";
import BooleanSetting from "components/SettingFields/atoms/BooleanSetting";
import Swal from "sweetalert2";
import InsightsFormHandler from "./InsightsFormHandler";

const Badge = ({ color, icon }) => (
  <MDBox
    variant="gradient"
    bgColor={color}
    color={color === "light" ? "dark" : "white"}
    coloredShadow={color}
    borderRadius="xl"
    display="flex"
    justifyContent="center"
    alignItems="center"
    width="4rem"
    height="4rem"
    mt={-6}
  >
    <Icon fontSize="medium" color="inherit">
      {icon}
    </Icon>
  </MDBox>
);

const InsightsFormTopComponent = ({
  settings,
  clonedSetting,
  setClonedSetting,
  handleCustomOptionModalOpen,
  enabledAlerts,
  setHasChanges,
  selectedInsightsConfigType = "company",
  hasChanges,
  saving,
  handleFormCancel,
  handleOnSubmit,
}) => {
  const [handleSubmit, setHandleSubmit] = useState(false);

  useEffect(() => {
    if (handleSubmit) {
      handleOnSubmit(undefined, undefined, true);
      setHandleSubmit(false);
    }
  }, [clonedSetting]);

  return (
    <Grid container spacing={2} pt={2}>
      {/* Settings */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 1 }}>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
            p={2}
          >
            <Badge color="dark" icon="reviews" />
            <MDBox sx={{ display: "flex", alignItems: "center" }}>
              <BooleanSetting
                setting={{
                  en_field_label: "",
                  field_value: clonedSetting?.[`${selectedInsightsConfigType}Enabled`] || false,
                }}
                setParentState={async (newChildValue) => {
                  await Swal.fire({
                    title: "Save Configuration",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    reverseButtons: true,
                    // eslint-disable-next-line no-nested-ternary
                    text: newChildValue
                      ? selectedInsightsConfigType === "company"
                        ? "You are enabling Company Default Settings. Personal settings cannot be used. This will not override other users settings."
                        : "You are enabling Personal Settings. Company Default Settings cannot be used. This will not override other users settings."
                      : selectedInsightsConfigType === "company"
                      ? "You are disabling Company Default Settings. This will not override other users settings."
                      : "You are disabling Personal Settings. This will not override other users settings.",
                  }).then(async ({ isConfirmed = false }) => {
                    if (isConfirmed) {
                      setHandleSubmit(true);
                      setClonedSetting((prev) => {
                        const clone = cloneDeep(prev);
                        clone[`${selectedInsightsConfigType}Enabled`] = !!newChildValue;
                        return clone;
                      });
                    }
                  });
                }}
                styles={{ marginBottom: 0 }}
              />
              <MDTypography variant="h4">
                {selectedInsightsConfigType === "company" ? "Company Default" : "Personal"} Settings
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox mb={2} px={2}>
            <MDTypography variant="body2">
              {selectedInsightsConfigType === "company"
                ? "These are your Company default settings for receiving insights. Any user who has Company default settings enabled will use these settings."
                : "These are your personal default settings for receiving insights. You can customize or adjust them below to better fit your preferences for each individual insight."}
            </MDTypography>
            <MDBox ml={2}>
              <List
                sx={{
                  listStyleType: "disc",
                  listStylePosition: "inside",
                }}
              >
                <ListItem sx={{ display: "list-item" }}>
                  <MDTypography variant="body2" component="span">
                    <b>Report on :</b> Select which teams you&apos;d like to receive insights for.
                  </MDTypography>
                </ListItem>
                <ListItem sx={{ display: "list-item" }}>
                  <MDTypography variant="body2" component="span">
                    <b>Trailing Average :</b> Determine how insights are compared to previous days.
                  </MDTypography>
                </ListItem>
                <ListItem sx={{ display: "list-item" }}>
                  <MDTypography variant="body2" component="span">
                    <b>Alert Type :</b> Choose how often you&apos;d like to receive alerts—daily,
                    weekly, or monthly.
                  </MDTypography>
                </ListItem>
              </List>
            </MDBox>
          </MDBox>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <MDBox sx={{ flex: 1 }} px={2}>
              <MultiSelectDropdown
                name="defaultReportOn"
                setting={{
                  columnId: "defaultReportOn",
                  en_field_label: "Report On - Default",
                  field_value: clonedSetting?.defaultValues?.teamIds || [],
                  settings_data_type: "multi-select-dropdown",
                  options: settings?.dropDownOptions?.teamIds || [],
                }}
                disableCloseOnSelect
                setParentState={(newChildValue) => {
                  setClonedSetting((prev) => {
                    const clone = cloneDeep(prev);
                    clone.defaultValues.teamIds = newChildValue;
                    return clone;
                  });
                  setHasChanges(true);
                }}
              />
              <SingleSelectDropdown
                setting={{
                  columnId: "defaultPeriod",
                  en_field_label: "Trailing Average - Default",
                  field_value: clonedSetting?.defaultValues?.period || null,
                  settings_data_type: "single-select-dropdown",
                  options: settings?.dropDownOptions?.period || [],
                }}
                setParentState={(newChildValue) => {
                  if (newChildValue?.value === "custom") {
                    handleCustomOptionModalOpen({
                      for: "trailingAverage",
                      pathToUpdate: ["defaultValues", "period"],
                      optionToUpdate: "period",
                    });
                  } else {
                    setClonedSetting((prev) => {
                      const clone = cloneDeep(prev);
                      clone.defaultValues.period = newChildValue;
                      return clone;
                    });
                    setHasChanges(true);
                  }
                }}
              />
              <SingleSelectDropdown
                setting={{
                  columnId: "defaultAlertType",
                  en_field_label: "Alert Type - Default",
                  field_value: clonedSetting?.defaultValues?.interval || null,
                  settings_data_type: "multi-select-dropdown",
                  options: settings?.dropDownOptions?.interval || [],
                }}
                passWholeOption
                setParentState={(newChildValue) => {
                  setClonedSetting((prev) => {
                    const clone = cloneDeep(prev);
                    clone.defaultValues.interval = newChildValue;
                    return clone;
                  });
                  setHasChanges(true);
                }}
              />
            </MDBox>
            <MDBox px={2} sx={{ display: "flex", alignItems: "flex-end" }}>
              <MultiBooleanSetting
                setting={{
                  columnId: "deliveryMechanisms",
                  field_value: [
                    {
                      value: clonedSetting?.deliveryMechanisms?.email ?? true,
                      display: "Email",
                    },
                    {
                      value: clonedSetting?.deliveryMechanisms?.inApp ?? true,
                      display: "In App",
                    },
                  ],
                  en_field_label: "",
                  settings_data_type: "multi-boolean",
                  options: [],
                }}
                passWholeOption
                setParentState={(newChildValue) => {
                  setClonedSetting((prev) => {
                    const clone = cloneDeep(prev);
                    clone.deliveryMechanisms = newChildValue.reduce((obj, child) => {
                      switch (child.display) {
                        case "Email":
                          obj.email = child.value;
                          break;
                        case "In App":
                          obj.inApp = child.value;
                          break;
                        default:
                          break;
                      }

                      return obj;
                    }, {});

                    return clone;
                  });

                  setHasChanges(true);
                }}
                styles={{
                  display: "flex",
                  flexDirection: "column",
                }}
                formControlStyle={{ flexDirection: "row" }}
              />
            </MDBox>
          </MDBox>
          <InsightsFormHandler
            hasChanges={hasChanges}
            saving={saving}
            handleFormCancel={handleFormCancel}
            styles={{ marginRight: "1rem", marginBottom: "1rem" }}
          />
        </Card>
      </Grid>
      {/* Enabled Alerts */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 1, height: "100%" }}>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
            p={2}
          >
            <Badge color="primary" icon="leaderboard" />
            <MDTypography variant="h4">
              {selectedInsightsConfigType === "company" ? "Company " : ""}Enabled Alerts
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              alignItems: "center",
              height: "100%",
            }}
          >
            <MDTypography variant="body1">
              Daily Alerts - {enabledAlerts?.daily || 0} of alerts enabled
            </MDTypography>
            <MDTypography variant="body1">
              Weekly Alerts - {enabledAlerts?.weekly || 0} of alerts enabled
            </MDTypography>
            <MDTypography variant="body1">
              Monthly Alerts - {enabledAlerts?.monthly || 0} of alerts enabled
            </MDTypography>
            <MDTypography variant="body1">
              {enabledAlerts?.kpis || 0} of KPIs not enabled
            </MDTypography>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
};

export default InsightsFormTopComponent;
